import * as coreActions from '../actions/core.actions';
import * as principalsActions from '../actions/principals.actions';

import { Action, createReducer, on } from '@ngrx/store';
import {
  AppSection,
  NomLanguages,
  NomObjectClass,
  NomObjectLifecycleStatus,
} from '../constants';
import { DataObject, Language, User } from '../models/core.models';
import {
  createFeed,
  createFeedFail,
  createFeedSuccess,
} from '../actions/feed.actions';
import {
  loginSuccess,
  logout,
  switchUserCompany,
} from '../actions/auth.actions';

import { ModuleOption } from '../components/module-switch/module-switch.component';
import { Principal } from '../models/object.models';
import { SortDirection } from 'ag-grid-community';
import { UserAccessLevel } from '../services/user.service';
import { produce } from 'immer';
import { setHomeObjectsLifeCycle } from '../actions/home.actions';
import { setWorkspaceObjectsLifeCycle } from '../actions/workspace.actions';

export interface CoreState {
  error: any; // You can specify a more specific type if you have one for errors.
  darkMode: boolean;
  hasShadow: boolean;
  homeObjectsLifeCycle: number;
  workspaceObjectsLifeCycle: number;
  filteredObjectsSortingField: string;
  filteredObjectsSortingDirection: SortDirection;
  appSection: AppSection;
  modules: ModuleOption[];
  language: Language;
  creatingFeed: boolean;
}

export const initialState: CoreState = {
  hasShadow: false,
  error: null,
  darkMode: false,
  homeObjectsLifeCycle: NomObjectLifecycleStatus.ACTIVE,
  workspaceObjectsLifeCycle: NomObjectLifecycleStatus.ACTIVE,
  filteredObjectsSortingField: 'title',
  filteredObjectsSortingDirection: 'asc',
  appSection: 'analytics',
  modules: [
    {
      id: 1,
      name: 'Analytics',
      nameKey: 'module-switch.module.analytics',
      icon: ['fas', 'chart-column'],
      url: '/analytics',
      active: true,
      accessLevel: UserAccessLevel.CLIENT_USER,
    },
    {
      id: 2,
      name: 'Admin',
      nameKey: 'module-switch.module.admin',
      icon: ['fas', 'lock'],
      url: '/admin',
      active: false,
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
    },
    {
      id: 3,
      name: 'Source Catalog',
      nameKey: 'module-switch.module.source-catalog',
      icon: ['fas', 'rss'],
      url: '/source-catalog',
      active: false,
      accessLevel: UserAccessLevel.SYSTEM_ADMIN,
    },
    {
      id: 4,
      name: 'Sysadmin',
      nameKey: 'module-switch.module.sysadmin',
      icon: ['fas', 'screwdriver-wrench'],
      url: '/sysadmin',
      active: false,
      accessLevel: UserAccessLevel.SYSTEM_ADMIN,
    },
    {
      id: 5,
      name: 'Mobile App',
      nameKey: 'module-switch.module.mobile-app',
      icon: ['fas', 'mobile-screen-button'],
      url: '/mobile-admin',
      active: false,
      accessLevel: UserAccessLevel.PLATFORM_ADMIN,
    },
  ],

  language: {
    id: NomLanguages.ENGLISH,
    name: 'English',
    code: 'en',
    rtl: false,
  },
  creatingFeed: false,
};
const reducer = createReducer(
  initialState,

  // * Reset the state to initial state on logout
  on(logout, () => initialState),

  on(
    createFeed,
    produce((draft) => {
      draft.creatingFeed = true;
    }),
  ),

  on(
    createFeedSuccess,
    produce((draft) => {
      draft.creatingFeed = false;
    }),
  ),

  on(
    createFeedFail,
    produce((draft) => {
      draft.creatingFeed = false;
    }),
  ),

  on(coreActions.toggleDarkMode, (state) => ({
    ...state,
    darkMode: !state.darkMode,
  })),

  on(loginSuccess, (state, { response }) => ({
    ...state,
    darkMode: response.user.dark_mode,
    language: response.user.language,
  })),

  on(principalsActions.loadCompanyPrincipals, (state) => ({
    ...state,
    error: null,
  })),

  on(coreActions.showShadow, (state) => {
    return {
      ...state,
      hasShadow: true,
    };
  }),

  on(coreActions.hideShadow, (state) => {
    return {
      ...state,
      hasShadow: false,
    };
  }),

  on(coreActions.setAppSection, (state, { appSection, menuItems }) => {
    return {
      ...state,
      appSection: appSection,
    };
  }),

  on(setHomeObjectsLifeCycle, (state, { homeObjectsLifeCycle }) => {
    return {
      ...state,
      homeObjectsLifeCycle,
    };
  }),

  on(setWorkspaceObjectsLifeCycle, (state, { workspaceObjectsLifeCycle }) => {
    return {
      ...state,
      workspaceObjectsLifeCycle,
    };
  }),
  on(
    coreActions.switchActiveModule,
    produce((draft, { moduleId }) => {
      for (const module of draft.modules) {
        module.active = module.id === moduleId;
      }
    }),
  ),

  on(coreActions.setCoreLanguage, (state, { language }) => {
    return {
      ...state,
      language,
    };
  }),

  on(switchUserCompany, (state) => {
    return {
      ...state,
      error: null,
    };
  }),

  on(
    coreActions.setCoreFilteredObjectsSortingField,
    (state, { sortingField }) => {
      return {
        ...state,
        filteredObjectsSortingField: sortingField,
      };
    },
  ),

  on(
    coreActions.setCoreFilteredObjectsSortingDirection,
    (state, { sortingDirection }) => {
      return {
        ...state,
        filteredObjectsSortingDirection: sortingDirection,
      };
    },
  ),
);

export function coreReducer(state: CoreState | undefined, action: Action) {
  return reducer(state, action);
}
